"use client";

import IconCrossLarge from "@ui/central-icons/icons/IconCrossLarge";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { pxToRem } from "@ui/lib/utils";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  Dialog as AriaDialog,
  Heading,
  type ModalOverlayProps,
  Button,
  Pressable,
  type ButtonProps,
} from "react-aria-components";

export { DialogTrigger } from "react-aria-components";

export function DialogButton(
  props:
    | ({ asChild?: false } & ButtonProps)
    | ({ asChild: true } & Pick<React.ComponentProps<typeof Pressable>, "children">),
) {
  if (props.asChild) {
    const { asChild: _asChild, ...rest } = props;
    return <Pressable {...rest} />;
  }
  const { asChild: _asChild, ...rest } = props;
  return <Button {...rest} />;
}

// On iOS Safari, position:fixed overlays stay at the full layout viewport height when
// the virtual keyboard opens, so content behind the keyboard can't be scrolled to.
// Adding paddingBottom equal to the keyboard height keeps the overlay full-screen (no
// clipping) while giving overflow-y:auto enough scrollable space to reach every input.
function useKeyboardAwareOverlay() {
  const [style, setStyle] = useState<React.CSSProperties | undefined>();

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      const keyboardHeight = window.innerHeight - vv.height;
      setStyle(keyboardHeight > 100 ? { paddingBottom: `${keyboardHeight}px` } : undefined);
    };

    vv.addEventListener("resize", update);
    return () => vv.removeEventListener("resize", update);
  }, []);

  return style;
}

export function Dialog({
  placement = "center",
  title,
  titleClassName,
  children,
  width,
  onKeyDown,
  ...props
}: Omit<ModalOverlayProps, "children" | "className" | "style"> & {
  title: string;
  titleClassName?: string;
  children: React.ReactNode;
  placement?: "center" | "right" | "bottom";
  width?: number;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
}) {
  const t = useTranslations("ClientKeys.Globals");
  const keyboardStyle = useKeyboardAwareOverlay();

  return (
    <ModalOverlay
      {...props}
      style={keyboardStyle}
      className={({ isExiting }) =>
        clsx(
          "tw-fixed tw-inset-0 tw-isolate tw-z-[9999] tw-overflow-x-hidden tw-overflow-y-auto",
          isExiting && "tw-opacity-0 tw-transition-opacity tw-duration-300",
        )
      }
    >
      {({ isEntering }) => (
        <>
          <div
            className={clsx(
              "tw-fixed tw-inset-0 tw-bg-cream/80 tw-backdrop-blur-sm tw-transition-opacity tw-duration-300",
              isEntering && "tw-opacity-0",
            )}
          />
          <div
            className={clsx(
              "tw-relative tw-z-10 tw-flex tw-min-h-full tw-w-full tw-flex-col",
              placement === "center" && "sm:tw-px-2 sm:tw-py-12",
              placement === "right" && "sm:tw-p-1",
              placement === "bottom" && "tw-justify-end",
            )}
          >
            <div
              className={clsx(
                "tw-max-w-full tw-transition tw-ease-in-out tw-delay-100 tw-duration-500 tw-flex",
                placement === "center" && ["tw-my-auto tw-mx-auto tw-w-max tw-p-4", isEntering && "tw-translate-y-1"],
                placement === "right" && [
                  "tw-my-auto tw-ml-auto tw-flex-auto tw-w-max tw-p-4",
                  isEntering && "-tw-translate-x-1",
                ],
                placement === "bottom" && [
                  "tw-mt-auto tw-mx-auto tw-w-full tw-p-4 tw-pt-1",
                  isEntering && "tw-translate-y-2",
                ],
              )}
              style={{
                transitionProperty: "mask-position, transform",
                ...(placement === "right"
                  ? {
                      maskImage: "linear-gradient(to left, white 33.333333%, transparent 66.666666%)",
                      maskSize: "300% 100%",
                      maskPosition: isEntering ? "0% 0%" : "100% 0%",
                    }
                  : {
                      maskImage: "linear-gradient(to bottom, white 33.333333%, transparent 66.666666%)",
                      maskSize: "100% 300%",
                      maskPosition: isEntering ? "0% 100%" : "0% 0%",
                    }),
              }}
            >
              <Modal
                {...props}
                className={clsx(
                  "tw-relative tw-bg-paper tw-rounded-2.5xl sm:tw-rounded-4xl tw-max-w-full tw-py-8 tw-px-6 sm:tw-p-14",
                )}
                style={{
                  width: pxToRem(width ?? 672),
                  boxShadow:
                    "0 0 1px 0 rgba(0, 0, 0, 0.40), 0 2px 4px 0 rgba(0, 0, 0, 0.04), 0 6px 16px 0 rgba(0, 0, 0, 0.04)",
                }}
              >
                <AriaDialog
                  className='tw-outline-none'
                  render={
                    onKeyDown
                      ? // renderProps includes role="dialog" and tabIndex from useDialog;

                        renderProps => <section {...renderProps} onKeyDown={onKeyDown} />
                      : undefined
                  }
                >
                  <div className='tw-pointer-events-none tw-absolute tw-inset-y-3 tw-right-3 tw-z-20'>
                    <Button
                      slot='close'
                      aria-label={t("close")}
                      className='tw-pointer-events-auto tw-sticky tw-top-3 tw-size-10 sm:tw-size-11 tw-rounded-full tw-bg-white tw-flex tw-items-center tw-justify-center tw-text-black hover:tw-bg-gray-25 tw-transition-colors tw-outline-none focus-visible:tw-outline-focus'
                      style={{
                        boxShadow:
                          "0 0 1px 0 rgba(0, 0, 0, 0.40), 0 1px 1px 0 rgba(0, 0, 0, 0.04), 0 2px 4px 0 rgba(0, 0, 0, 0.04)",
                      }}
                    >
                      <IconCrossLarge className={clsx("tw-size-4", iconStrokeFix)} />
                    </Button>
                  </div>
                  <Heading
                    slot='title'
                    className={clsx(
                      titleClassName ?? "tw-type-4xl",
                      "tw-text-black tw-text-pretty tw-pr-16 sm:tw-pr-4",
                    )}
                  >
                    {title}
                  </Heading>
                  <div className='tw-isolate'>{children}</div>
                </AriaDialog>
              </Modal>
            </div>
          </div>
        </>
      )}
    </ModalOverlay>
  );
}
